import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import dayOfYear from 'dayjs/plugin/dayOfYear';
import localeData from 'dayjs/plugin/localeData';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import shortTimezoneAbbreviation from './plugins/shortTimezoneAbbreviation/shortTimezoneAbbreviation';

// Configure dayjs with all required plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(dayOfYear);
dayjs.extend(localizedFormat);
dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);
dayjs.extend(relativeTime);
dayjs.extend(localeData);
dayjs.extend(shortTimezoneAbbreviation);

const localeMapping = Object.freeze({
  'zh-Hans': 'zh-cn',
  'zh-Hant': 'zh-tw',
});

// Set the locale based on the page's I18n settings
if (window.I18n?.locale) {
  const locale = localeMapping[window.I18n.locale] || window.I18n.locale;

  // Get the original locale data
  const originalLocale = dayjs.Ls?.[locale] || dayjs.Ls?.en;
  const ordinalFn = originalLocale?.ordinal;

  if (ordinalFn) {
    // By default, dayjs wraps ordinal numbers in brackets (e.g., "[1st]", "[2nd]")
    // This differs from Moment.js which returned ordinals without brackets
    // During migration from Moment.js to Day.js, we need to maintain the same behavior
    // to avoid breaking changes in the UI
    //
    // We configure this here, in the dayjs setup, because:
    // 1. It ensures consistent ordinal formatting across the entire application
    // 2. It prevents the need for string manipulation in individual components
    // 3. It avoids test-specific overrides or mocks
    // 4. It makes tests reflect the actual behavior of the application
    // 5. It centralizes this formatting decision in one place
    // 6. It maintains compatibility with our previous Moment.js implementation
    dayjs.locale(
      {
        ...originalLocale,
        ordinal: (number: number) => {
          const ordinal = ordinalFn(number);

          return String(ordinal).replace(/\[|\]/g, '');
        },
      },
      locale,
    );
  }
}

// Export the configured instance
export default dayjs;
